import React, {
  createContext, useContext, useMemo, useEffect
} from 'react';
import {
  node, any, string, func, shape
} from 'prop-types';
import {
  useDataModel, extend, alias, string as stringType, arrayOf as arrayOfType
} from '@thd-nucleus/data-sources';
import { dataModel } from '../components/dataModel';
import { transformItemDataToPromoModel } from '../utils/promo-model-utils';
import { sortItemToTopOfList } from '../utils/promo-utils';

export const PromoModelContext = createContext();
export const usePromoModel = () => useContext(PromoModelContext);

export const PromoModelProvider = ({
  anchorItem, store, setIsBatchLoading, productDataModel, children
}) => {

  // We are limited to calls with 16 items and need to ensure that anchor is part of those 16
  let itemIds = (anchorItem.pricing.promotion.itemList || []).slice();
  itemIds = sortItemToTopOfList(itemIds, anchorItem.itemId).slice(0, 16);
  const mergedDataModel = extend({}, productDataModel, dataModel.discountedMultipleItemId);
  const dataModelQuery = {
    discountedMultipleItemId: alias('products').params({
      itemIds: arrayOfType(stringType().isRequired()).isRequired(),
      dataSource: stringType()
    }).shape(mergedDataModel)
  };
  const { data, loading } = useDataModel('discountedMultipleItemId', {
    variables: {
      itemIds,
      storeId: store.storeId,
      dataSource: 'discounted-items',
    },
    dataModel: dataModelQuery,
    ssr: false,
  });

  useEffect(() => setIsBatchLoading(loading), [loading, setIsBatchLoading]);

  const rawProducts = data?.products;

  const contextValue = useMemo(() => {
    const promoModel = transformItemDataToPromoModel(rawProducts, anchorItem);

    return {
      store,
      anchorItem,
      allFulfillableProductsAndAnchor: promoModel.allFulfillableProductsAndAnchor,
      isBogo: promoModel.isBogo,
      isMsb: promoModel.isMsb,
      isBmsm: promoModel.isBmsm,
      analyticsComponent: promoModel.analyticsComponent,
      analyticsSharedSection: promoModel.analyticsSharedSection,
      displayableItemIds: promoModel.displayableItemIds,
      hasProducts: promoModel.hasProducts,
      singleItemIdToDisplay: promoModel.singleItemIdToDisplay,
      isAnchorFulfillable: promoModel.isAnchorFulfillable,
      subExperience: promoModel.subExperience,
      rewardTiers: promoModel.rewardTiers,
      bogoMaxQuantity: 1, // TODO: link to dynamic data when available
    };
  }, [
    rawProducts,
    store,
    anchorItem,
  ]);

  return (
    <PromoModelContext.Provider value={contextValue}>
      { children }
    </PromoModelContext.Provider>
  );
};

PromoModelProvider.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  anchorItem: any,
  store: shape({
    storeId: string.isRequired,
    storeZip: string,
  }).isRequired,
  setIsBatchLoading: func.isRequired,
  children: node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  productDataModel: any,
};

PromoModelProvider.defaultProps = {
  anchorItem: {},
  productDataModel: {},
};
